import React, { useState, useEffect, useCallback } from "react";
import { Button, Input, Modal, notification } from "antd";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import {
  fetchProviderTelecom,
  fetchCardDataInfo,
  processDataPayment,
  fetchCheckMsisdn,
} from "../../services/topupService";
import VoucherSection from "../VoucherSection";
import { triggerMiniAppPayment } from "../../services/paymentUtils";

const DataPackage = ({ setLoading, loading, token }) => {
  const [networks, setNetworks] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [checkingPhone, setCheckingPhone] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cardData, setCardData] = useState({
    byDay: [],
    byHour: [],
    byMonth: [],
    other: [],
  });
  const [manualProviderSelection, setManualProviderSelection] = useState(false);
  const [isProviderLocked, setIsProviderLocked] = useState(false);
  const [voucherValue, setVoucherValue] = useState(0);
  const [voucherCode, setVoucherCode] = useState("");

  useEffect(() => {
    const stored = sessionStorage.getItem("userInfo");
    if (stored) {
      try {
        const userInfo = JSON.parse(stored);
        if (userInfo?.phone) {
          const phone = userInfo.phone.replace(/^84/, "0");
          setPhoneNumber(phone);
        }
      } catch { }
    }
  }, [token]);

  const formatVND = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  const validatePhoneNumber = (num) => {
    const cleaned = num.replace(/\D/g, "");
    if (!cleaned) return "Vui lòng nhập số điện thoại";
    if (cleaned.length < 10 || cleaned.length > 11) return "Số điện thoại không hợp lệ";
    return "";
  };

  const fetchCardDataByProvider = async (net) => {
    if (!net) return;
    setLoading(true);
    try {
      const res = await fetchCardDataInfo(net.providercode);
      const raw = res?.data?.data || {};
      setCardData({
        byDay: (raw.carddatabyday || []).filter((item) => item.status === 1),
        byHour: (raw.carddatabyhour || []).filter((item) => item.status === 1),
        byMonth: (raw.carddatabymonth || []).filter((item) => item.status === 1),
        other: (raw.carddataother || []).filter((item) => item.status === 1),
      });
    } catch (error) {
      setCardData({ byDay: [], byHour: [], byMonth: [], other: [] });
    } finally {
      setLoading(false);
    }
  };

  const checkMsisdn = useCallback(async (num) => {
    const cleaned = num.replace(/\D/g, "");
    const error = validatePhoneNumber(cleaned);
    if (error) {
      setPhoneError(error);
      return;
    }
    setPhoneError("");
    setCheckingPhone(true);
    try {
      const res = await fetchCheckMsisdn(cleaned);
      if (res?.data?.data?.msisdn) {
        const returnedProvider = res.data.data.provider?.toLowerCase() || "";
        const matched = networks.find((n) =>
          n.providername?.toLowerCase().includes(returnedProvider) ||
          returnedProvider.includes(n.providername?.toLowerCase())
        );
        if (matched && !manualProviderSelection) {
          setSelectedProvider(matched);
          setSelectedCard(null);
          setIsProviderLocked(true);
          fetchCardDataByProvider(matched);
        }
        setPhoneError("");
      } else {
        setPhoneError("Số điện thoại không hợp lệ");
      }
    } catch (err) {
      setPhoneError("Không thể xác thực số điện thoại");
    } finally {
      setCheckingPhone(false);
    }
  }, [networks]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (phoneNumber.replace(/\D/g, "").length >= 10) {
        checkMsisdn(phoneNumber);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [phoneNumber, checkMsisdn]);

  const handleProviderChange = async (net) => {
    if (isProviderLocked) return;
    setSelectedProvider(net);
    setSelectedCard(null);
    setShowConfirm(false);
    setVoucherCode("");
    setVoucherValue(0);
    await fetchCardDataByProvider(net);
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    if (val.replace(/\D/g, "").length > 11) return;
    setPhoneNumber(val);
    setPhoneError("");
    setManualProviderSelection(false);
    setIsProviderLocked(false);
  };

  const handleCardSelect = (card) => {
    setSelectedCard(card);
  };

  const handleConfirmPayment = () => {
    const phoneErr = validatePhoneNumber(phoneNumber.replace(/\D/g, ""));
    if (phoneErr) {
      setPhoneError(phoneErr);
      return;
    }
    if (!selectedCard) return;
    setShowConfirm(true);
  };

  const handleCancelConfirm = () => {
    setShowConfirm(false);
  };

  const handlePayment = async () => {
    if (!selectedCard) return;
    setShowConfirm(false);
    setLoading(true);
    try {
        const paymentData = {
         Msisdn: phoneNumber.replace(/\D/g, ""),
         ProviderCode: selectedProvider.providercode,
         DataServiceId: selectedCard.id,
         DataServiceCode: selectedCard.serviceprovidercode,
         Amount: Math.max(0, selectedCard.productamount - voucherValue),
         VoucherCode: voucherCode.trim(),
       };
      paymentData.InfoOrder = JSON.stringify({
        Service: "datamobile",
        Sponsor: "IPASS",
        ProductName: selectedCard.productname,
        PhoneNumber: phoneNumber.replace(/\D/g, ""),
        Amount: paymentData.Amount,
        VoucherCode: voucherCode.trim() || "",
        VoucherValue: voucherValue || 0,
        AmountItem: paymentData.Amount,
      });
      const paymentResponse = await processDataPayment(paymentData);
      if (paymentResponse?.data?.data?.ordercode) {
        await triggerMiniAppPayment(paymentResponse.data.data.ordercode, paymentData.Amount);
      } else {
        notification.error({
          message: "Thanh toán thất bại",
          description: paymentResponse?.data?.message || "Đã xảy ra lỗi khi thanh toán",
        });
      }
    } catch (error) {
      notification.error({
        message: "Thanh toán thất bại",
        description: error?.response?.data?.message || error?.response?.data?.err || "Đã xảy ra lỗi khi xử lý thanh toán",
      });
    } finally {
      setLoading(false);
    }
  };

  const isPhoneValid = phoneNumber.replace(/\D/g, "").length >= 10 && !phoneError && !checkingPhone;

  const canSubmit = isPhoneValid && selectedCard && !loading;

  useEffect(() => {
    if (token) {
      const loadNetworks = async () => {
        try {
          const res = await fetchProviderTelecom();
          const data = (res?.data?.data || res?.data || []).filter((n) => n.status === 1);
          setNetworks(data);
          if (data.length > 0) {
            handleProviderChange(data[0]);
          }
        } catch (err) {
          setNetworks([]);
        }
      };
      loadNetworks();
    }
  }, [token]);

  const renderConfirmModal = () => {
    return (
      <Modal
        open={showConfirm}
        onCancel={handleCancelConfirm}
        title={<span className="text-lg font-bold text-gray-800">Xác nhận mua gói data</span>}
        footer={
          <div className="flex gap-3">
            <Button
              onClick={handleCancelConfirm}
              className="flex-1"
            >
              Hủy
            </Button>
            <Button
              type="primary"
              onClick={handlePayment}
              loading={loading}
              className="flex-1"
            >
              Xác nhận
            </Button>
          </div>
        }
        className="!rounded-t-3xl"
      >
        <p className="text-sm text-gray-500 mb-5">Vui lòng kiểm tra thông tin trước khi thanh toán</p>

        <div className="bg-gray-50 rounded-2xl p-4 mb-5 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Số điện thoại</span>
            <span className="text-sm font-semibold text-gray-800">{phoneNumber}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Nhà mạng</span>
            <span className="text-sm font-semibold text-gray-800">
              {selectedProvider?.providername || selectedProvider?.providercode}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Gói data</span>
            <span className="text-sm font-semibold text-gray-800">
              {selectedCard?.productname}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Thanh toán</span>
            <span className="text-base font-bold text-primary">
              {formatVND(selectedCard?.productamount)}
            </span>
          </div>
          {voucherValue > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">Giảm giá</span>
              <span className="text-green-600 font-medium">-{formatVND(voucherValue)}</span>
            </div>
          )}
          <div className="flex justify-between items-center border-t border-gray-200 pt-2">
            <span className="text-sm font-semibold text-gray-800">Thành tiền</span>
            <span className="text-lg font-bold text-primary">
              {formatVND(Math.max(0, selectedCard?.productamount - voucherValue))}
            </span>
          </div>
        </div>
      </Modal>
    );
  };

  const renderPackageSection = (title, data) => {
    if (!data || data.length === 0) return null;
    return (
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 px-1 mb-3">
          {title}
        </label>
        <div className="grid grid-cols-3 gap-3">
          {data.map((pkg) => {
            const isActive = selectedCard?.id === pkg.id;
            return (
              <Button
                key={pkg.id}
                onClick={() => handleCardSelect(pkg)}
                variant={isActive ? "solid" : "outlined"}
                type={isActive ? "primary" : "default"}
                className="!rounded-xl !font-medium !py-3 !px-2 !h-auto !flex-col !gap-1"
              >
                <p className={`font-bold text-[10px] ${isActive ? "!text-white" : "text-gray-800"}`}>
                  {pkg.productname}
                </p>
                {pkg.productdata && (
                  <p className="text-[10px] font-medium">{pkg.productdata}</p>
                )}
                <p className={`font-bold text-sm ${isActive ? "!text-white" : "text-gray-800"}`}>
                  {formatVND(pkg.productamount)}
                </p>
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  const hasAnyData =
    cardData.byHour.length > 0 ||
    cardData.byDay.length > 0 ||
    cardData.byMonth.length > 0 ||
    cardData.other.length > 0;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white p-4 rounded-2xl shadow-sm mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Số điện thoại nhận Data
        </label>
        <div className="relative flex items-center mb-4">
          <Input
            status={phoneError ? "error" : isPhoneValid ? "success" : undefined}
            size="large"
            className="!rounded-xl !text-lg"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="Nhập số điện thoại"
            allowClear
          />
        </div>

        {checkingPhone && (
          <div className="flex items-center gap-1.5 mb-2">
            <Loader2 size={14} className="text-primary animate-spin" />
            <span className="text-xs text-blue-500">Đang kiểm tra số điện thoại...</span>
          </div>
        )}
        {phoneError && (
          <div className="flex items-center gap-1.5 mb-2">
            <AlertCircle size={14} className="text-red-500" />
            <span className="text-xs text-red-500">{phoneError}</span>
          </div>
        )}
        {isPhoneValid && (
          <div className="flex items-center gap-1.5 mb-2">
            <CheckCircle size={14} className="text-green-500" />
            <span className="text-xs text-green-500">Số điện thoại hợp lệ</span>
          </div>
        )}

        {networks.length > 0 && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Chọn nhà mạng
            </label>
            <div className="grid grid-cols-4 gap-2">
              {networks.map((net) => {
                const isActive = selectedProvider?.providercode === net.providercode;
                return (
                  <Button
                    key={net.providercode}
                    onClick={() => handleProviderChange(net)}
                    disabled={isProviderLocked}
                    ghost={true}
                    className={`py-3 px-2 rounded-xl text-[11px] font-bold transition-all duration-200 flex flex-col items-center gap-2 h-auto ${isActive
                      ? isProviderLocked
                        ? "!bg-primary-50 !border !border-primary !text-primary shadow-sm opacity-90 cursor-not-allowed"
                        : "!bg-primary-50 !border !border-primary !text-primary shadow-sm"
                      : isProviderLocked
                        ? "opacity-30 cursor-not-allowed !bg-white !border !border-gray-200 !text-gray-400"
                        : "!bg-white !border !border-gray-200 !text-gray-500 hover:!border-primary hover:!text-primary"
                      }`}
                  >
                    <div className="w-15 h-14 flex items-center justify-center">
                      {net.link ? (
                        <img
                          src={`${process.env.REACT_APP_BASE_URL}${net.link}`}
                          alt={net.providername}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span>{net.providername}</span>
                      )}
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {hasAnyData && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-6">
          {renderPackageSection("GÓI DATA THEO GIỜ", cardData.byHour)}
          {renderPackageSection("GÓI DATA THEO NGÀY", cardData.byDay)}
          {renderPackageSection("GÓI DATA THEO THÁNG", cardData.byMonth)}
          {renderPackageSection("GÓI DATA KHÁC", cardData.other)}

          {selectedCard && (
            <div className="border-t border-gray-100 pt-4 mt-4">
              <VoucherSection
                total={selectedCard?.productamount || 0}
                quantity={1}
                disabled={!selectedCard}
                resetKey={selectedProvider?.providercode}
                onChange={({ code, value }) => {
                  setVoucherCode(code);
                  setVoucherValue(value);
                }}
              />
            </div>
          )}
        </div>
      )}

      {!hasAnyData && (
        <div className="bg-white p-4 rounded-2xl shadow-sm mb-6">
          <div className="text-center py-6 text-gray-400 text-sm">
            Không có gói cước nào
          </div>
        </div>
      )}

      {selectedCard && (
        <Button
          type="primary"
          onClick={handleConfirmPayment}
          disabled={!canSubmit}
          loading={loading}
          className="w-full !rounded-xl !shadow-lg !py-4 !h-auto"
        >
          <span className="text-white font-bold text-lg"> Mua gói {formatVND(Math.max(0, selectedCard.productamount - voucherValue))}</span>
        </Button>
      )}



      {renderConfirmModal()}
    </div>
  );
};

export default DataPackage;